import { useMutation, useQueryClient } from 'react-query';
import {
  openerOrchestratorAxiosApi,
  predictionManagerAxiosApi,
  unwinderOrchestratorAxiosApi
} from 'src/Config/axiosApi';
import { SYSTEM_ALL_STATE_KEY } from 'src/Common/hooks/useSystemKillSwitch';

export type OrchestratorDomain = 'opener' | 'unwinder';

/**
 * `predictionManagerAxiosApi` is created with NO timeout. A hung boot would
 * otherwise leave `isLoading` true forever — the CTA stuck disabled, and past
 * the gate's own deadline the operator able to stack a second POST on top.
 */
export const ORCHESTRATOR_BOOT_TIMEOUT_MS = 30000;

const DOMAIN_STATE_KEY: Record<OrchestratorDomain, string> = {
  opener: 'opener-state',
  unwinder: 'unwinder-state'
};

const DOMAIN_API = {
  opener: openerOrchestratorAxiosApi,
  unwinder: unwinderOrchestratorAxiosApi
} as const;

/** How long to wait for a just-booted orchestrator to answer `/health`. */
export const ORCHESTRATOR_READY_TIMEOUT_MS = 90000;
/** Gap between readiness probes while waiting. */
export const ORCHESTRATOR_READY_POLL_MS = 1500;
/** A readiness probe must not hang — it is retried anyway. */
const READY_PROBE_TIMEOUT_MS = 4000;

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * Is the domain's orchestrator answering? A pod scaled to 0 has no endpoints,
 * so this rejects (connection refused / 503) rather than returning a status.
 */
async function isOrchestratorReachable(
  domain: OrchestratorDomain
): Promise<boolean> {
  try {
    await DOMAIN_API[domain].get('/health', {
      timeout: READY_PROBE_TIMEOUT_MS
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Make sure the domain's control plane is up, booting it if it is not.
 *
 * The orchestrator pods default to 0 replicas (the all-off cost floor), and the
 * routes behind START / rule activation are served BY those pods — so a start
 * against a scaled-to-zero orchestrator just fails. This used to be the
 * operator's problem: press "Boot control plane", wait, then press "Start".
 * That is plumbing, not a decision, so START now does it.
 *
 * Boot is idempotent server-side, but we probe first anyway: the common case is
 * an already-running control plane, and that path should cost one GET, not a
 * scale round-trip.
 *
 * Returns whether it actually had to boot, so callers can say so.
 */
export async function ensureOrchestratorBooted(
  domain: OrchestratorDomain
): Promise<{ booted: boolean }> {
  if (await isOrchestratorReachable(domain)) return { booted: false };

  // `intent=domain-start` claims the pod under the DOMAIN START's own key,
  // not the operator's. This boot is a STEP OF START — the routes START needs
  // are served by the pod itself, so it has to come up first — and claiming
  // the operator's "boot to configure" key here left a claim that STOP has no
  // business releasing: the pod then stayed up forever after a START/STOP
  // round trip, which is the whole defect this flow exists to avoid.
  //
  // OPENER ONLY, on purpose. `unwinder-orchestrator/boot` has no `intent`
  // binding and its STOP writes no intent at all, so sending it there would
  // be silently dropped — and asserting it in a test would cement a contract
  // the backend does not implement. Wire the unwinder's own release first if
  // its START ever needs this.
  const intent = domain === 'opener' ? '?intent=domain-start' : '';
  await predictionManagerAxiosApi.post(
    `/api/system/${domain}-orchestrator/boot${intent}`,
    undefined,
    { timeout: ORCHESTRATOR_BOOT_TIMEOUT_MS }
  );

  const deadline = Date.now() + ORCHESTRATOR_READY_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await sleep(ORCHESTRATOR_READY_POLL_MS);
    if (await isOrchestratorReachable(domain)) return { booted: true };
  }

  // Fail loudly rather than firing START at a pod that is not there — the
  // caller surfaces this, instead of the operator seeing a bare network error.
  throw new Error(
    `The ${domain} control plane did not come up within ` +
      `${Math.round(ORCHESTRATOR_READY_TIMEOUT_MS / 1000)}s. It may be waiting ` +
      `for node capacity — check the ${domain}-orchestrator pod.`
  );
}

/**
 * Boot / shut down a domain's orchestrator (control plane) pod.
 *
 * The knob lives on the MANAGER base (`predictionManagerAxiosApi`), NEVER on
 * the orchestrator it controls — a pod scaled to 0 cannot serve its own boot.
 *
 * Kept in its own tiny module (rather than in `useEntry` / `useUnwinding`) so
 * the boot CTA can be rendered from any screen without dragging a rule-CRUD
 * module graph along with it.
 */
export function useBootOrchestrator(domain: OrchestratorDomain) {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await predictionManagerAxiosApi.post(
        `/api/system/${domain}-orchestrator/boot`,
        undefined,
        { timeout: ORCHESTRATOR_BOOT_TIMEOUT_MS }
      );
      // The backend answers 202 even on a failed boot (`{ ok: false, error }`)
      // — the intent write and the scale call are reported independently, so
      // neither can be collapsed into an HTTP status. Throwing here is what
      // turns that into a mutation `onError`; every caller (this hook has two:
      // `BootDomainGate`'s auto-recovery and the explicit boot button) already
      // reacts to `isError`, or gets it for free from this single choke point.
      if (data?.ok === false) {
        throw new Error(data.error ?? `${domain} boot failed`);
      }
      return data;
    },
    {
      mutationKey: `${domain}:boot-orchestrator`,
      onSuccess: () => {
        qc.invalidateQueries([DOMAIN_STATE_KEY[domain]]);
        qc.invalidateQueries([SYSTEM_ALL_STATE_KEY]);
      }
    }
  );
}

/** Mirror of `useBootOrchestrator` — also on the MANAGER base. */
export function useShutdownOrchestrator(domain: OrchestratorDomain) {
  const qc = useQueryClient();
  return useMutation(
    async () => {
      const { data } = await predictionManagerAxiosApi.post(
        `/api/system/${domain}-orchestrator/shutdown`,
        undefined,
        { timeout: ORCHESTRATOR_BOOT_TIMEOUT_MS }
      );
      // Same reasoning as `useBootOrchestrator`: the drain gate can refuse
      // (`{ ok: false, reason }`, e.g. the engine is not provably stopped) or
      // the scale call itself can fail after the intent already cleared
      // (`{ ok: false, error }`) — both still 202. Without this throw the
      // operator sees the button return to idle and has no way to tell the
      // pod is still up.
      if (data?.ok === false) {
        throw new Error(data.reason ?? data.error ?? `${domain} shutdown failed`);
      }
      return data;
    },
    {
      mutationKey: `${domain}:shutdown-orchestrator`,
      onSuccess: () => {
        qc.invalidateQueries([DOMAIN_STATE_KEY[domain]]);
        qc.invalidateQueries([SYSTEM_ALL_STATE_KEY]);
      }
    }
  );
}
